"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useHotelStore } from "@/shared/stores/hotel-store/HotelStoreProvider";
import type { HotelDetailV2BookingModel } from "../model/hotelDetailV2.model";
import {
  createHotelPassengersUrl,
  getSelectedRoomsCount,
  getSelectedRoomsTotal,
} from "../utils/bookingNavigation";

interface MobileBookingBarProps {
  booking: HotelDetailV2BookingModel;
  city: string;
  slug: string;
}

export default function MobileBookingBar({
  booking,
  city,
  slug,
}: MobileBookingBarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const rooms = useHotelStore((state) => state.rooms);
  const selectedCount = getSelectedRoomsCount(rooms);
  const selectedTotal = getSelectedRoomsTotal(rooms);
  const canContinue =
    booking.available &&
    selectedCount > 0 &&
    rooms.length > 0 &&
    Boolean(booking.accommodationKey);

  const handleAction = () => {
    if (!canContinue) {
      document.getElementById("rooms")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    router.push(
      createHotelPassengersUrl({
        city,
        slug,
        booking,
        selectedRooms: rooms,
        currentQuery: searchParams.toString(),
      }),
    );
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-[#e5e7eb] bg-white/95 px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_rgba(15,23,42,0.1)] backdrop-blur lg:hidden">
      <div className="mx-auto flex max-w-md items-center gap-3" dir="rtl">
        <div className="min-w-0 flex-1">
          {selectedTotal > 0 ? (
            <>
              <p className="text-[11px] text-[#6b7280]">
                {selectedCount.toLocaleString("fa-IR")} اتاق انتخاب‌شده
              </p>
              <p className="truncate text-sm font-extrabold text-[#1a1a2e]">
                {selectedTotal.toLocaleString("fa-IR")} تومان
              </p>
            </>
          ) : booking.available && booking.totalPrice ? (
            <>
              <p className="text-[11px] text-[#6b7280]">شروع قیمت</p>
              <p className="truncate text-sm font-extrabold text-[#1a1a2e]">
                {booking.totalPrice.toLocaleString("fa-IR")} تومان
              </p>
            </>
          ) : (
            <p className="truncate text-sm font-bold text-[#6b7280]">
              {booking.availabilityMessage || "تاریخ اقامت را انتخاب کنید"}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleAction}
          className="flex h-11 min-w-[148px] items-center justify-center rounded-lg bg-primary px-4 text-sm font-bold text-white"
          data-analytics-event="cta_click"
          data-analytics-id={
            canContinue
              ? "hotel-detail-v2-mobile-continue-booking"
              : "hotel-detail-v2-mobile-select-room"
          }
          data-analytics-name={canContinue ? "ادامه رزرو" : "انتخاب اتاق"}
          data-analytics-type="button"
          data-analytics-funnel="hotel_booking"
          data-analytics-step={
            canContinue ? "detail_continue_booking" : "detail_select_room_cta"
          }
          data-analytics-property-city={city}
          data-analytics-property-hotel-slug={slug}
          data-analytics-property-selected-count={String(selectedCount)}
          data-analytics-property-selected-total={String(selectedTotal)}
        >
          {canContinue ? "ادامه رزرو" : "انتخاب اتاق"}
        </button>
      </div>
    </div>
  );
}
